import { useState, useEffect, useCallback } from 'react';

export default function useSortRules({ columns, defaultSort }) {
  const [sortRules, setSortRules] = useState(
    defaultSort ? [defaultSort] : [{ field: columns[0]?.id || '', direction: 'desc' }]
  );
  const [sortPopoverOpen, setSortPopoverOpen] = useState(false);

  const sortableColumns = columns.filter((c) => c.sortable);

  const handleColumnSort = useCallback((colId) => {
    const col = columns.find((c) => c.id === colId);
    if (!col || !col.sortable) return;

    setSortRules((prev) => {
      const existing = prev.length === 1 && prev[0].field === colId;
      if (existing) {
        return [{ field: colId, direction: prev[0].direction === 'asc' ? 'desc' : 'asc' }];
      }
      return [{ field: colId, direction: 'asc' }];
    });
  }, [columns]);

  const handleSortRuleChange = useCallback((index, key, value) => {
    setSortRules((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [key]: value };
      return updated;
    });
  }, []);

  const handleAddSortRule = useCallback(() => {
    const usedFields = new Set(sortRules.map((r) => r.field));
    const nextCol = sortableColumns.find((c) => !usedFields.has(c.id));
    if (nextCol) {
      setSortRules((prev) => [...prev, { field: nextCol.id, direction: 'asc' }]);
    }
  }, [sortRules, sortableColumns]);

  const handleRemoveSortRule = useCallback((index) => {
    setSortRules((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  const sortArrow = useCallback((colId) => {
    const rule = sortRules.find((r) => r.field === colId);
    if (!rule) return null;
    const index = sortRules.indexOf(rule);
    const arrow = rule.direction === 'asc' ? '\u2191' : '\u2193';
    return sortRules.length > 1 ? ` ${index + 1}${arrow}` : ` ${arrow}`;
  }, [sortRules]);

  useEffect(() => {
    if (!sortPopoverOpen) return;
    const handleClickOutside = (e) => {
      if (!e.target.closest('.reporting-sort-popover')) {
        setSortPopoverOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [sortPopoverOpen]);

  return {
    sortRules,
    setSortRules,
    sortPopoverOpen,
    setSortPopoverOpen,
    sortableColumns,
    handleColumnSort,
    handleSortRuleChange,
    handleAddSortRule,
    handleRemoveSortRule,
    sortArrow,
  };
}
